import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";

import UploadProgressSnackbar from "../../components/upload-progress-snackbar/UploadProgressSnackbar";
import UploadStatus from "../../components/upload-status/UploadStatus";
import { Success, Warning, Error } from "../svg/svgImageList";
import useThemeService from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import { Dialog } from "../dialog/Dialog";
import { uploadFilesService, UploadStatusOption } from "../../services/upload/UploadFilesService";
import { useLeavePagePrompt, LeavePagesEnum } from "../../helpers/useLeavePagePrompt";
import "./composition.scss";

export default observer(function UploadStatusOverview() {
  const { themeClass } = useThemeService();

  const uploadStatus = uploadFilesService.uploadProgress.uploadStatus;

  const succesfullUpload = useMemo((): any => {
    return (
      uploadStatus.failed === 0 &&
      uploadFilesService.uploadProgress.status !== UploadStatusOption.failed &&
      uploadFilesService.uploadProgress.status !== UploadStatusOption.undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFilesService.uploadProgress.status, uploadStatus]);

  const [setLeavePagePrompt] = useLeavePagePrompt();

  useEffect(() => {
    setLeavePagePrompt(uploadFilesService.uploadProgress.status === UploadStatusOption.ongoing, LeavePagesEnum.Upload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFilesService.uploadProgress.status]);

  return (
    <>
      <UploadProgressSnackbar />
      <Dialog
        isVisible={uploadFilesService.uploadProgressModalIsVisbile}
        onHide={uploadFilesService.hideUploadProgress}
        titleKey="general.upload.in-progress-title"
        sidePosition
        buttonOk={{
          id: "upload-status-ok",
          textKey: "general.ok",
          action: uploadFilesService.hideUploadProgress,
        }}
      >
        <UploadStatus />
      </Dialog>
      <Dialog
        isVisible={uploadFilesService.uploadSummaryModalIsVisible && succesfullUpload}
        onHide={uploadFilesService.hideUploadSummaryModal}
        titleKey="general.upload.title-success"
        iconTitle={{
          svg: Success,
          width: 30,
          height: 30,
          className: themeClass(["icon-success"]),
        }}
        buttonOk={{
          textKey: "general.upload.view-details",
          action: uploadFilesService.showUploadProgress,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: uploadFilesService.hideUploadSummaryModal,
        }}
      >
        <div className={themeClass(["subtext-dialog"])}>
          {translationService.translate("general.upload.success", {
            filesCount: uploadStatus.completed + uploadStatus.completedWithWarnings,
          })}
        </div>
      </Dialog>
      <Dialog
        isVisible={
          uploadFilesService.uploadSummaryModalIsVisible &&
          !succesfullUpload &&
          (uploadStatus.completed !== 0 || uploadStatus.completedWithWarnings !== 0)
        }
        onHide={uploadFilesService.hideUploadSummaryModal}
        titleKey="general.upload.title-partial-fail"
        iconTitle={{
          svg: Warning,
          width: 30,
          height: 30,
          className: themeClass(["icon-warning"], ["partially-failed"]),
        }}
        buttonOk={{
          textKey: "general.upload.view-details",
          action: uploadFilesService.showUploadProgress,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: uploadFilesService.hideUploadSummaryModal,
        }}
      >
        <div className={themeClass(["subtext-dialog"])}>
          <div>
            {translationService.translate("general.upload.success", {
              filesCount: uploadStatus.completed + uploadStatus.completedWithWarnings,
            })}
          </div>
          <div>
            {translationService.translate("general.upload.fail", {
              filesCount: uploadStatus.failed,
            })}
          </div>
        </div>
      </Dialog>
      <Dialog
        isVisible={
          uploadFilesService.uploadSummaryModalIsVisible &&
          !succesfullUpload &&
          uploadStatus.completed === 0 &&
          uploadStatus.completedWithWarnings === 0 &&
          uploadFilesService.uploadProgress.errorKey !== "general.upload.no-storage"
        }
        onHide={uploadFilesService.hideUploadSummaryModal}
        titleKey="general.upload.title-fail"
        iconTitle={{
          svg: Error,
          width: 30,
          height: 30,
          className: themeClass(["icon-error"], ["failed"]),
        }}
        buttonOk={{
          textKey: "general.upload.view-details",
          action: uploadFilesService.showUploadProgress,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: uploadFilesService.hideUploadSummaryModal,
        }}
      >
        <div className={themeClass(["subtext-dialog"])}>
          <div>
            {translationService.translate("general.upload.fail", {
              filesCount: uploadStatus.failed,
            })}
          </div>
        </div>
      </Dialog>
    </>
  );
});
